
import { useState, useEffect } from 'react';
import { ArxivMetadata } from '@/lib/api';
import { PaperAuthor } from '@/types/paper';
import { useExternalUrlValidation } from './useExternalUrlValidation';

export const useUploadMetadata = () => {
  // Paper metadata state
  const [title, setTitle] = useState('');
  const [abstract, setAbstract] = useState('');
  const [category, setCategory] = useState('');
  const [keywords, setKeywords] = useState<string[]>([]);
  const [additionalAuthors, setAdditionalAuthors] = useState<PaperAuthor[]>([]);
  const [publishedAt, setPublishedAt] = useState<string | undefined>(undefined);
  
  // URL state and validation
  const [arxivUrl, setArxivUrl] = useState('');
  const [externalUrl, setExternalUrl] = useState('');
  
  const { 
    validateUrl, 
    isValidating: isValidatingUrl, 
    isValid: isUrlValid,
    validationMessage: urlValidationMessage,
    metadata: arxivMetadata,
    license,
    licenseCompliant,
    licenseValidationError,
    duplicateInfo
  } = useExternalUrlValidation();
  
  // Apply arXiv metadata if available
  useEffect(() => {
    if (arxivMetadata) {
      if (arxivMetadata.title) setTitle(arxivMetadata.title);
      if (arxivMetadata.abstract) setAbstract(arxivMetadata.abstract);
      if (arxivMetadata.categories && arxivMetadata.categories.length > 0) {
        setCategory(arxivMetadata.categories[0]);
      }
      if (arxivMetadata.published) {
        setPublishedAt(arxivMetadata.published);
      }
      
      // Set authors from arXiv metadata - preserve original order
      if (arxivMetadata.authors && arxivMetadata.authors.length > 0) {
        const paperAuthors: PaperAuthor[] = arxivMetadata.authors.map((authorName, index) => ({
          id: `external-${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${index}`, // Add index to ensure unique IDs
          name: authorName,
          isPlatformUser: false,
          institution: '' // Empty institution for arXiv authors unless specified
        }));
        
        setAdditionalAuthors(paperAuthors);
      }
    }
  }, [arxivMetadata]);
  
  // Handle URL change
  useEffect(() => {
    if (arxivUrl) {
      validateUrl(arxivUrl);
    }
  }, [arxivUrl, validateUrl]);
  
  // Validate external URL
  const validateExternalUrl = async () => {
    if (externalUrl) {
      setArxivUrl(externalUrl);
    }
  };

  return {
    // Metadata state
    title,
    setTitle,
    abstract,
    setAbstract,
    category,
    setCategory,
    keywords,
    setKeywords,
    additionalAuthors,
    setAdditionalAuthors,
    
    // URL and validation
    arxivUrl,
    setArxivUrl,
    externalUrl,
    setExternalUrl,
    isValidatingUrl,
    isUrlValid,
    urlValidationMessage,
    arxivMetadata,
    validateExternalUrl,
    
    // License information
    license,
    licenseCompliant,
    licenseValidationError,
    
    // Duplicate information
    duplicateInfo,
    
    // Published date from arXiv
    publishedAt,
    
    // Derived state
    shouldShowMetadataForm: isUrlValid
  };
};
